import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { endsWith } from 'lodash';
import { Checkbox, FormControlLabel, Typography, Stack } from '@mui/material';
import { comparableTypes, getCompItemTitlePrice, reportTypes } from 'lib/PropertyUtils';
import { truncateString } from 'lib/StringUtils';
import DaysOnMarket from 'components/DaysOnMarket';
import { DragHandle } from 'components/DragHandle';
import EditableSalePrice from 'components/EditableSalePrice';
import FormattedDate from 'components/FormattedDate';
import Optional from 'components/Optional';
import bathIcon from 'images/bath-icon.svg';
import bedIcon from 'images/bed-icon.svg';
import carIcon from 'images/car-icon.svg';
import binIcon from 'images/delete.svg';
import listingsMarker from 'images/featured_listings_marker.svg';
import rentalsMarker from 'images/featured_rentals_marker.svg';
import salesMarker from 'images/featured_sales_marker.svg';
import './Title.scss';
import SelectedTitleColumns from '../SelectedTitleColumns/SelectedTitleColumns';
import SalePrice from 'components/SalePrice';
import InlineArea from 'components/InlineArea/InlineArea';
import ResultTitleColumns from 'components/ComparablePropertyAccordion/ResultTitleColumns/ResultTitleColumns';

const Title = ({
                   isSelected,
                   isSelectView,
                   property,
                   comparableType,
                   featured,
                   index,
                   reportType,
                   onAddFeatured,
                   onRemoveFeatured,
                   onRemoveSelected,
                   onSelectChange,
               }) => {
    const [isSales, setIsSales] = useState('sales' === comparableType);
    useEffect(() => setIsSales('sales' === comparableType), [comparableType]);

    const onFeaturedCheckboxChange = (event, checked) => {
        if (!checked) {
            onRemoveFeatured();
        } else {
            onAddFeatured();
        }
        event.stopPropagation();
    };

    const onDeleteClick = (event) => {
        onRemoveSelected();
        event.stopPropagation();
    };

    const getMarkerUrl = () => {
        return {
            sales: salesMarker,
            listings: listingsMarker,
            rentals: rentalsMarker,
        }[comparableType];
    };

    function resultColumn1() {
        return (
            <div>
                <FormControlLabel
                    aria-label="Select comparable property"
                    onChange={(e, checked) => {
                        onSelectChange(e, {checked});
                    }}
                    onClick={(event => event.stopPropagation())}
                    onFocus={(event) => event.stopPropagation()}
                    control={
                        <Checkbox
                            classes={{root: 'property-select'}}
                            color="primary"
                            checked={isSelected}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: '22.5px',
                                },
                            }}
                        />
                    }
                    label=""
                />
            </div>
        );
    }

    function selectedColumn1() {
        return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
                <DragHandle/>
                <span
                    className="selected-index"
                    style={{background: `url(${getMarkerUrl()}) no-repeat center/33px 27px`}}
                >
                    {index + 1}
                </span>
                <Typography
                    className="address"
                    title={property.singleLineAddress}
                    variant="fontWeight600"
                    color="textPrimary"
                >
                    {property.singleLineAddress}
                </Typography>
            </Stack>
        );
    }

    function resultColumn2() {
        return (
            <Typography
                className="address"
                title={property.singleLineAddress}
                variant="fontWeight600"
                color="textPrimary"
            >
                {property.singleLineAddress}
            </Typography>
        );
    }

    function selectedColumn2() {
        return (
            <FormControlLabel
                aria-label="Feature comparable property"
                onChange={onFeaturedCheckboxChange}
                onClick={(event => event.stopPropagation())}
                onFocus={(event) => event.stopPropagation()}
                control={
                    <Checkbox
                        classes={{root: 'property-featured'}}
                        color="primary"
                        checked={featured}
                        sx={{
                            '& .MuiSvgIcon-root': {
                                fontSize: '22.5px',
                            },
                        }}
                    />
                }
                label=""
                sx={{ marginRight: 0 }}
            />
        );
    }

    function column3() {
        return (
            <Typography
                variant="fontWeight600"
            >
                <span className="land-area">
                    <InlineArea area={property.landArea}/>
                </span>
            </Typography>

        );
    }

    function resultColumn3() {
        return column3();
    }

    function selectedColumn3() {
        return column3();
    }

    function column4() {
        if (isSales) {
            if ('soi' === reportType) {
                return (
                    <Typography
                        variant="fontWeight600"
                    >
                        <EditableSalePrice property={property}/>
                    </Typography>

                );
            } else {
                return (
                    <Typography
                        variant="fontWeight600"
                    >
                        <SalePrice isPriceWithheld={property.isPriceWithheld}
                                   isAgentsAdvice={property.isAgentsAdvice}
                                   salePrice={property.salePrice}/>
                    </Typography>
                );
            }
        }
        const propertyPriceToUse = getCompItemTitlePrice(comparableType, property);
        const listingPrice = truncateString(propertyPriceToUse, 45);
        const title = endsWith(listingPrice, '...') ? propertyPriceToUse : null;
        return (
            <Typography
                className="listing-price"
                title={title}
                color="textPrimary"
                variant="fontWeight600"
            >
                {listingPrice}
            </Typography>
        );
    }

    function resultColumn4() {
        return column4();
    }

    function selectedColumn4() {
        return column4();
    }

    function column5() {
        return (
            <span className={isSales ? 'sale-date' : 'listing-date'}>
              <Typography
                  variant="fontWeight600"
              >
                    <FormattedDate
                        date={property.saleDate}
                    />
              </Typography>

            </span>
        );
    }

    function resultColumn5() {
        return column5();
    }

    function selectedColumn5() {
        return column5();
    }

    function column6() {
        return (
            <span className="days-on-market">
                <Typography
                    variant="fontWeight600"
                >
                    <DaysOnMarket daysOnMarket={property.daysOnMarket}/>
                </Typography>
            </span>
        );
    }

    function resultColumn6() {
        return column6();
    }

    function selectedColumn6() {
        return column6();
    }

    function column7() {
        return (
            <div className="icons-column">
        <span className="icon-block bedrooms">
          <img src={bedIcon} alt="bedrooms icon"/>
          <Optional value={property.bedrooms}/>
        </span>
                <span className="icon-block bathrooms">
          <img src={bathIcon} alt="bathrooms icon"/>
          <Optional value={property.bathrooms}/>
        </span>
                <span className="icon-block car-spaces">
          <img src={carIcon} alt="car icon"/>
          <Optional value={property.carSpaces}/>
        </span>
                {isSelectView && (
                    <span className="icon-block delete-image" onClick={onDeleteClick}>
            <img src={binIcon} alt="remove selected property"/>
          </span>
                )}
            </div>
        );
    }

    function resultColumn7() {
        return column7();
    }

    function selectedColumn7() {
        return column7();
    }

    return isSelectView ? (
        <SelectedTitleColumns column1={selectedColumn1()}
                              column2={selectedColumn2()}
                              column3={selectedColumn3()}
                              column4={selectedColumn4()}
                              column5={selectedColumn5()}
                              column6={selectedColumn6()}
                              column7={selectedColumn7()}/>
    ) : (
        <ResultTitleColumns column1={resultColumn1()}
                            column2={resultColumn2()}
                            column3={resultColumn3()}
                            column4={resultColumn4()}
                            column5={resultColumn5()}
                            column6={resultColumn6()}
                            column7={resultColumn7()}/>
    );
};

Title.propTypes = {
    isSelected: PropTypes.bool,
    isSelectView: PropTypes.bool,
    property: PropTypes.shape({
        propertyId: PropTypes.number.isRequired,
        singleLineAddress: PropTypes.string.isRequired,
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        carSpaces: PropTypes.number,
        floorArea: PropTypes.number,
        landArea: PropTypes.number,
        yearBuilt: PropTypes.number,
        capitalValue: PropTypes.number,
        saleDate: PropTypes.string,
        salePrice: PropTypes.number,
        daysOnMarket: PropTypes.number,
        firstPublishedPrice: PropTypes.string,
        firstPublishedDate: PropTypes.string,
        latestPublishedPrice: PropTypes.string,
        isPriceWithheld: PropTypes.bool,
        isAgentsAdvice: PropTypes.bool,
        propertyPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
        uploadPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
        propertyPhotoUriSelectedIndex: PropTypes.number,
    }).isRequired,
    comparableType: PropTypes.oneOf(comparableTypes).isRequired,
    featured: PropTypes.bool.isRequired,
    index: PropTypes.number,
    reportType: PropTypes.oneOf(reportTypes),
    onAddFeatured: PropTypes.func.isRequired,
    onRemoveFeatured: PropTypes.func.isRequired,
    onRemoveSelected: PropTypes.func.isRequired,
    onSelectChange: PropTypes.func.isRequired,
};

Title.defaultProps = {
    isSelected: false,
    isSelectView: true,
    propertyDetailsSpacing: 6
};

export default Title;
